import classNames from 'classnames'
import { useEffect, useRef, useState } from 'react'
import { Button } from 'react-bootstrap'
import Spinner from 'react-bootstrap/Spinner'

import Icon, { EDHRECIconsType } from '@/components/Icon/Icon'
import { useAds } from '@/contexts/ads'
import { ComboVoteSubmission } from '@/types/combo'

import styles from './VoteButtonGroup.module.scss'

export const VOTE_STEP_INDEX = {
  EARLY_GAME: 0,
  EASY_PREREQ: 1,
  SIGNIFICANT_RESULT: 2,
  SUBMIT: 3,
}

type ResizableSectionProps = {
  children?: React.ReactNode
  className?: string
  currentExpanded: number
  growFromBottom?: boolean
  index: number
  onStepClick: (step: number) => void
}

const ResizableSection = ({
  children,
  className,
  currentExpanded,
  growFromBottom,
  index,
  onStepClick,
}: ResizableSectionProps) => {
  const isSelected = currentExpanded === index
  const containerRef = useRef<HTMLDivElement>(null)
  const [height, setHeight] = useState<number | undefined>(undefined)

  const updateHeight = (container: HTMLDivElement) => {
    const childrenHeight = Array.from(container.children).reduce(
      (totalHeight, child) => totalHeight + (child as HTMLElement).offsetHeight,
      0,
    )
    setHeight(childrenHeight + 34)
  }

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const resizeObserver = new ResizeObserver(() => {
      if (currentExpanded === index) {
        updateHeight(container)
      }
    })

    resizeObserver.observe(container)

    return () => {
      resizeObserver.disconnect()
    }
  }, [currentExpanded, index])

  useEffect(() => {
    if (currentExpanded === index) {
      const container = containerRef.current
      if (container) {
        updateHeight(container)
      }
    } else {
      setHeight(60)
    }
  }, [currentExpanded, index])

  return (
    <div
      className={classNames(styles.voteContainer, className, isSelected && styles.selected, styles.growFromBottom)}
      onClick={() => onStepClick(index)}
      style={{ height }}
    >
      <div className={classNames(styles.staticContainer, growFromBottom && styles.growFromBottom)} ref={containerRef}>
        {children}
      </div>
    </div>
  )
}

type VoteSectionProps = {
  currentExpanded: number
  currentVote?: boolean
  description: React.ReactNode
  handleVote: (vote: boolean) => void
  icon: EDHRECIconsType
  index: number
  onStepClick: (step: number) => void
  positionClassName: string
  title: string
}

const VoteSection = ({
  currentExpanded,
  currentVote,
  description,
  handleVote,
  icon,
  index,
  onStepClick,
  positionClassName,
  title,
}: VoteSectionProps) => {
  const handleVoteClick = (e: React.MouseEvent<HTMLButtonElement>, vote: boolean) => {
    e.stopPropagation()
    handleVote(vote)
  }

  return (
    <ResizableSection
      className={positionClassName}
      currentExpanded={currentExpanded}
      index={index}
      onStepClick={onStepClick}
    >
      <div className={styles.header}>
        <div className={classNames(styles.circleDiv, currentVote !== undefined && styles.voted)}>
          <Icon icon={currentVote === undefined ? icon : currentVote ? 'check' : 'xMark'} />
        </div>
        <h3>{title}</h3>
      </div>
      <div className='d-flex justify-content-center'>
        <p className={styles.description}>{description}</p>
      </div>
      <div className='d-flex gap-2 justify-content-center'>
        <Button
          className={classNames(currentVote !== undefined && !currentVote && styles.offVote)}
          onClick={(e) => handleVoteClick(e, true)}
          variant='success'
        >
          <Icon icon='check' /> Yes
        </Button>
        <Button
          className={classNames(currentVote && styles.offVote)}
          onClick={(e) => handleVoteClick(e, false)}
          variant='danger'
        >
          <Icon icon='xMark' /> No
        </Button>
      </div>
    </ResizableSection>
  )
}

type Props = {
  index: number
  loading?: boolean
  onIndexChange: (index: number) => void
  onSubmit: (vote: ComboVoteSubmission) => void
}

const VoteButtonGroup = ({ index, loading, onIndexChange, onSubmit }: Props) => {
  const { loadAds } = useAds()
  const [isEasyPrereq, setIsEasyPrereq] = useState<boolean>()
  const [isSignificantResult, setIsSignificantResult] = useState<boolean>()
  const [isEarlyGame, setIsEarlyGame] = useState<boolean>()

  const currentExpanded = index
  const setCurrentExpanded = onIndexChange

  const incompleteVote = isEarlyGame === undefined || isSignificantResult === undefined || isEasyPrereq === undefined

  const getNextAvailableStep = (currentStep: number) => {
    if (currentStep === 0) {
      if (isEasyPrereq === undefined) return VOTE_STEP_INDEX.EASY_PREREQ
      if (isSignificantResult === undefined) return VOTE_STEP_INDEX.SIGNIFICANT_RESULT
    }
    if (currentStep === 1) {
      if (isSignificantResult === undefined) return VOTE_STEP_INDEX.SIGNIFICANT_RESULT
      if (isEarlyGame === undefined) return VOTE_STEP_INDEX.EARLY_GAME
    }
    if (currentStep === 2) {
      if (isEarlyGame === undefined) return VOTE_STEP_INDEX.EARLY_GAME
      if (isEasyPrereq === undefined) return VOTE_STEP_INDEX.EASY_PREREQ
    }
    return 3
  }

  const handleEarlyGameVote = (vote: boolean) => {
    setIsEarlyGame(vote)
    setCurrentExpanded(getNextAvailableStep(VOTE_STEP_INDEX.EARLY_GAME))
  }
  const handlePrereqVote = (vote: boolean) => {
    setIsEasyPrereq(vote)
    setCurrentExpanded(getNextAvailableStep(VOTE_STEP_INDEX.EASY_PREREQ))
  }
  const handleResultVote = (vote: boolean) => {
    setIsSignificantResult(vote)
    setCurrentExpanded(getNextAvailableStep(VOTE_STEP_INDEX.SIGNIFICANT_RESULT))
  }
  const handleStepClick = (step: number) => {
    if (step !== currentExpanded) setCurrentExpanded(step)
    else setCurrentExpanded(-1)
  }

  let bracketMessage = ''
  if (isEasyPrereq && isSignificantResult && isEarlyGame)
    bracketMessage =
      'This combo has trivial prerequisites, a significant result, and can be played early game. It should only be played in Bracket 4 and 5.'
  else if (isEasyPrereq && isSignificantResult)
    bracketMessage = 'This combo is safe to use in Bracket 3 since it cannot be played early game.'
  else if (!isSignificantResult && !isEasyPrereq)
    bracketMessage =
      'Since this combo has non-trivial prerequisites and does not have a significant result, it is safe to use in any bracket.'
  else if (!isSignificantResult)
    bracketMessage = 'Since this combo does not have a game-winning result, it is safe to use in any bracket.'
  else if (!isEasyPrereq)
    bracketMessage = 'Since this combo has non-trivial prerequisites, it is safe to use in any bracket.'

  return (
    <>
      <div className={classNames(styles.fixedOuter, { [styles.patron]: loadAds === false })}>
        <div className={styles.groupContainer}>
          <VoteSection
            currentExpanded={currentExpanded}
            currentVote={isEarlyGame}
            description='Can this combo be reasonably executed in the first six or so turns?'
            handleVote={handleEarlyGameVote}
            icon='clock'
            index={VOTE_STEP_INDEX.EARLY_GAME}
            onStepClick={handleStepClick}
            positionClassName={styles.top}
            title='Early Game'
          />
          <VoteSection
            currentExpanded={currentExpanded}
            currentVote={isEasyPrereq}
            description={
              <>
                Are the prerequisites (ignoring mana) easy enough that you&#39;d still call this a <strong>two</strong>
                -card combo?
              </>
            }
            handleVote={handlePrereqVote}
            icon='listCheck'
            index={VOTE_STEP_INDEX.EASY_PREREQ}
            onStepClick={handleStepClick}
            positionClassName={styles.middle}
            title='Prerequisites'
          />
          <VoteSection
            currentExpanded={currentExpanded}
            currentVote={isSignificantResult}
            description='Is it easy to leverage this combo into a win or a lockout?'
            handleVote={handleResultVote}
            icon='infinity'
            index={VOTE_STEP_INDEX.SIGNIFICANT_RESULT}
            onStepClick={handleStepClick}
            positionClassName={styles.middle}
            title='Results'
          />
          <ResizableSection
            className={styles.bottom}
            currentExpanded={currentExpanded}
            growFromBottom
            index={VOTE_STEP_INDEX.SUBMIT}
            onStepClick={handleStepClick}
          >
            <div className='d-flex justify-content-center'>
              <p className={classNames(styles.description)}>
                {incompleteVote ? <i>Please complete all questions before submitting</i> : bracketMessage}
              </p>
            </div>

            <div className='d-flex justify-content-center'>
              <Button
                className='mt-2'
                disabled={incompleteVote}
                onClick={() =>
                  onSubmit({
                    isEarlyGame: !!isEarlyGame,
                    isEasyPrereq: !!isEasyPrereq,
                    isSignificantResult: !!isSignificantResult,
                  })
                }
                variant={incompleteVote ? 'secondary' : 'success'}
              >
                {loading ? <Spinner /> : 'Submit Vote'}
              </Button>
            </div>
          </ResizableSection>
        </div>
      </div>
      <div className={styles.adBumper} />
    </>
  )
}

export default VoteButtonGroup
